/* eslint-disable no-unused-vars */
import {
  Button,
  Select,
  Text,
  Group,
  Notification,
  LoadingOverlay,
  rem,
  CheckIcon,
  CloseButton,
  Loader,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { AddressCard } from './Cards/AddressCard';
import { PersonInfoCard } from './Cards/PersonInfoCard';
import { ContactPersonCard } from './Cards/ContactPersonCard';
import { StartCareDateCard } from './Cards/StartCareDateCard';
import { CareRequirementsCard } from './Cards/CareRequirementsCard';
import { ProcessModal } from './Cards/ProcessModal';
import { AuditsModal } from './Cards/AuditsModal';
import { dateFormat, dateFormatUI, locale } from '@/index';
import { useDisclosure } from '@mantine/hooks';
import { createFormContext, zodResolver } from '@mantine/form';
import { useNavigate, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { getFetcher, postFetcher, putFetcher } from '@/api/fetchers';
import React, { useEffect, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type DateValue } from '@mantine/dates';
import { downloadFileWithBearerToken, isEmptyForm } from '@/utils/functions';
import { notifications } from '@mantine/notifications';
import { AttachmentCard } from './Cards/AttachmentCard';
import { modals } from '@mantine/modals';
import { convertToPutApplicant } from '@/schemas/converters/applicantSchemasConverter';
import { type Services } from '@/schemas/API/GET/service/response';
import {
  type ApplicantDetailed,
  applicantDetailedSchema,
} from '@/schemas/API/GET/applicant/response';
import {
  type ApplicantAttachments,
  type ApplicantAttachment,
  applicantAttachmentSchema,
} from '@/schemas/API/GET/applicant/attachment/response';
import { type Mapping } from '@/schemas/API/GET/mapping/response';
import { Badge } from '@/components/Badge/Badge';
import { displayError } from '@/utils/error/errorHandler';
import { useExportStatus } from '@/hooks/export/useExportStatus';

export const [
  ApplicantFormProvider,
  useApplicantFormContext,
  useApplicantForm,
] = createFormContext<ApplicantDetailed>();

export const [
  AttachmentFormProvider,
  useAttachmentFormContext,
  useAttachmentForm,
] = createFormContext<ApplicantAttachment[]>();

export const ApplicantFormPage: React.FC = () => {
  const navigate = useNavigate();

  const [
    openedProcessModal,
    { open: openProcessModal, close: closeProcessModal },
  ] = useDisclosure(false);
  const [
    openedAuditsModal,
    { open: openAuditsModal, close: closeAuditsModal },
  ] = useDisclosure(false);

  const { applicantId } = useParams<{ applicantId: string }>();
  const { checkExportStatus } = useExportStatus();

  const { data: mapState } = useSWR<Mapping>(
    'mappings/applicant-states',
    getFetcher
  );
  const {
    data: applicant,
    mutate,
    error,
  } = useSWR<ApplicantDetailed>(`/applicants/${applicantId}`, getFetcher);
  const { data: availableServices } = useSWR<Services>('services', getFetcher);
  const { data: attachments } = useSWR<ApplicantAttachments>(
    `/applicants/${applicantId}/attachments`,
    getFetcher
  );

  const form = useApplicantForm({
    validate: zodResolver(applicantDetailedSchema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  const attachmentForm = useAttachmentForm({
    validate: zodResolver(applicantAttachmentSchema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (!applicant) return;
    form.setInitialValues(applicant);
    form.setValues(applicant);
  }, [applicant]);

  useEffect(() => {
    if (!attachments) return;
    attachmentForm.setInitialValues(attachments.content);
    attachmentForm.setValues(attachments.content);
  }, [attachments]);

  const updateApplicantsForm = async (): Promise<void> => {
    const validationResult = form.validate();

    if (validationResult.hasErrors) {
      console.error('Form failed validation:', validationResult.errors);
      notifications.show({
        title: `Formulář zájemce se nepodařilo uložit.`,
        message: `Formulář obsahuje chyby.`,
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
      return;
    }

    try {
      const updateFn = async (): Promise<ApplicantDetailed> => {
        const applicant = convertToPutApplicant(form.values);
        return await putFetcher(`/applicants/${applicantId}`, applicant);
      };

      await mutate(updateFn(), {
        optimisticData: form.values,
        revalidate: false,
        rollbackOnError: true,
      });

      notifications.show({
        message: `Formulář zájemce byl úspěšně uložen.`,
        color: 'green',
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
    } catch (error) {
      displayError(error);
    }
  };

  const handleCancel = (): void => {
    if (!form.isDirty()) {
      navigate('/applicants');
      return;
    }
    modals.openConfirmModal({
      title: 'Odejít bez uložení?',
      centered: true,
      children: (
        <Text>
          Formulář obsahuje změny, které nebyly uloženy. Určitě chcete formulář
          uzavřít?
        </Text>
      ),
      labels: { confirm: 'Odejít', cancel: 'Zrušit' },
      confirmProps: { color: 'blue' },
      onCancel: () => {},
      onConfirm: () => {
        navigate('/applicants');
      },
    });
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleGeneratePDF = (): void => {
    if (isExporting) return;
    if (!form.isDirty()) {
      setIsExporting(true);
      void generatePDF();
    } else {
      modals.openConfirmModal({
        title: 'Vygenerovat PDF?',
        centered: true,
        children: (
          <Text>
            Formulář obsahuje změny, které nebyly uloženy. Určitě chcete
            vygenerovat PDF?
          </Text>
        ),
        labels: { confirm: 'Vygenerovat', cancel: 'Zrušit' },
        confirmProps: { color: 'blue' },
        onCancel: () => {},
        onConfirm: () => {
          setIsExporting(true);
          void generatePDF();
        },
      });
    }
  };

  const generatePDF = async (): Promise<void> => {
    try {
      const pdfCreateRequest = { id: applicantId };
      const response = await postFetcher(
        `/exports/applicants/forms`,
        pdfCreateRequest
      );
      const { exportId } = response;

      const pollExportStatus = async (): Promise<void> => {
        try {
          const exportStatusResponse = await checkExportStatus(exportId);
          if (
            exportStatusResponse?.state === 'FINISHED' &&
            exportStatusResponse?.url
          ) {
            await downloadFileWithBearerToken(exportStatusResponse.url);
          } else {
            displayError(`Export failed.`);
          }
        } catch (err) {
          displayError(err);
        } finally {
          setIsExporting(false);
        }
      };

      await pollExportStatus();
    } catch (err) {
      displayError(err);
    }
  };

  const disablePersonalFields = (): boolean => {
    return applicant?.clientId !== null;
  };

  const disableAllFields = (): boolean => {
    return (
      form.values.currState === 'CANCELLED' ||
      form.values.currState === 'INACTIVE' ||
      form.values.currState === 'ACTIVE'
    );
  };

  const getStateBadge = (state: string) => {
    let color = 'error';
    let actualState = '';
    switch (state) {
      case 'Aktivní':
        color = 'success';
        actualState = 'ACTIVE';
        break;
      case 'V pořadníku':
        actualState = 'QUEUED';
        color = 'warning';
        break;
      case 'Čekající':
        actualState = 'WAITING';
        color = 'warning';
        break;
      case 'Storno':
        actualState = 'CANCELLED';
        break;
      case 'Neaktivní':
        actualState = 'INACTIVE';
        break;
      default:
        break;
    }

    return (
      <Badge label={mapState ? mapState[actualState] : ''} color={color} />
    );
  };

  if (error) {
    console.error(`Error while fetching applicant ${applicantId}:`, error);

    const errIcon = <IconX style={{ width: rem(20), height: rem(20) }} />;

    return (
      <Notification
        radius="md"
        withBorder
        withCloseButton={false}
        icon={errIcon}
        color="red"
        title="Jejda!"
        styles={{
          root: {
            boxShadow: 'none',
            height: '15%',
            width: '50%',
            marginTop: '20%',
          },
        }}
      >
        Něco se pokazilo
      </Notification>
    );
  }

  if (!isEmptyForm(form)) {
    return (
      <ApplicantFormProvider form={form}>
        <div className="p-8">
          <div className="flex flex-col sm:rounded-card sm:border sm:border-card sm:p-2.5 gap-2.5">
            <Group justify="space-between">
              <Group>
                <Text fw={700} size="xl">
                  Formulář zájemce
                </Text>
                <Group className="mt-1 ml-5">
                  <Text>Stav:</Text>
                  {getStateBadge(mapState?.[form.values.currState] ?? '')}
                </Group>
              </Group>
              <CloseButton onClick={() => handleCancel()} />
            </Group>

            <Group justify="space-between">
              <DateInput
                className="w-full max-w-[235px]"
                variant="filled"
                label="Datum komunikace"
                placeholder="Vyberte datum"
                valueFormat={dateFormatUI}
                locale={locale}
                value={dayjs(form.values.commDate, dateFormat).toDate()}
                onChange={(d: DateValue) => {
                  const date: string = dayjs(d).format(dateFormat);
                  form.setFieldValue('commDate', date);
                }}
                error={form.getInputProps('commDate').error}
                disabled={disableAllFields()}
              />
              <Select
                label="Služba"
                value={form.values.serviceId.toString()}
                onChange={e => {
                  form.setFieldValue('serviceId', parseInt(e ?? '0'));
                }}
                data={availableServices?.map(service => ({
                  value: service.id.toString(),
                  label: service.name,
                }))}
                disabled={disableAllFields()}
                error={form.getInputProps('serviceId').error}
              />
            </Group>

            <PersonInfoCard
              title="Zájemce o službu"
              disabled={disablePersonalFields() || disableAllFields()}
              formContext={useApplicantFormContext}
            />

            <Group justify="space-between" grow className="flex items-start">
              <AddressCard
                addressType="Trvalá adresa"
                disabled={disablePersonalFields() || disableAllFields()}
                formContext={useApplicantFormContext}
              />
              <AddressCard
                addressType="Adresa poskytování"
                disabled={disablePersonalFields() || disableAllFields()}
                formContext={useApplicantFormContext}
              />
            </Group>

            <ContactPersonCard
              title="Kontaktující"
              formContext={useApplicantFormContext}
              disabled={disableAllFields()}
            />

            <StartCareDateCard
              formContext={useApplicantFormContext}
              disabled={disableAllFields()}
            />

            <CareRequirementsCard
              formContext={useApplicantFormContext}
              disabled={disableAllFields()}
            />

            <AttachmentFormProvider form={attachmentForm}>
              <AttachmentCard
                serviceId={form.values.serviceId}
                formContext={useAttachmentFormContext}
                disabled={disableAllFields()}
              />
            </AttachmentFormProvider>

            <Group justify="flex-end">
              <Button variant="outline" onClick={openAuditsModal}>
                Zobrazit historii úprav
              </Button>

              <Button
                variant="outline"
                onClick={handleGeneratePDF}
                disabled={isExporting}
              >
                {isExporting ? <Loader size="sm" /> : 'Vygenerovat PDF'}
              </Button>

              <Button variant="outline" onClick={openProcessModal}>
                Zpracovat
              </Button>

              <Button variant="outline" onClick={handleCancel}>
                Zrušit
              </Button>

              {!disableAllFields() && (
                <Button
                  className="w-28"
                  onClick={() => {
                    void updateApplicantsForm();
                  }}
                >
                  Uložit
                </Button>
              )}
            </Group>

            <ProcessModal
              opened={openedProcessModal}
              close={closeProcessModal}
            />
            <AuditsModal
              opened={openedAuditsModal}
              close={closeAuditsModal}
              type="applicants"
              id={applicantId}
            />
          </div>
        </div>
      </ApplicantFormProvider>
    );
  }

  return (
    <div className="relative" style={{ height: '100%', width: '100%' }}>
      <LoadingOverlay
        visible
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2 }}
      />
    </div>
  );
};
